// i18n core — every UI string in the app resolves through here.
// Catalogs are static JSON under src/locales/<locale>/<namespace>.json and are
// bundled by Vite (import.meta.glob eager), so language files ship inside the
// bundle. English is the source of truth; es-ES / pt-PT are small override
// catalogs that fall back to their base (es-419 / pt-BR) before falling back
// to English.
//
// Kept byte-identical to the desktop's app/src/i18n/index.js so catalog work
// syncs both ways with a plain copy. The `window.api?.setLanguage?.()` calls
// below are the Electron main-process bridge — undefined in a browser, so they
// no-op here; localStorage stays the store of record either way (i18n rule 9:
// the language choice must work before any sign-in).
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

export const LANGUAGE_STORAGE_KEY = 'pse_language';

// Picker order. Native labels stay in their own language — never translated.
export const LANGUAGES = [
  { code: 'en', label: 'English', englishName: 'English' },
  { code: 'es-419', label: 'Español (Latinoamérica)', englishName: 'Spanish (Latin America)' },
  { code: 'es-ES', label: 'Español (España)', englishName: 'Spanish (Spain)' },
  { code: 'fr', label: 'Français', englishName: 'French' },
  { code: 'pt-BR', label: 'Português (Brasil)', englishName: 'Portuguese (Brazil)' },
  { code: 'pt-PT', label: 'Português (Portugal)', englishName: 'Portuguese (Portugal)' },
];

const SUPPORTED = LANGUAGES.map((lang) => lang.code);

// Eagerly bundle every catalog file; dropping a new JSON into src/locales
// auto-registers it — no import list to maintain.
const catalogModules = import.meta.glob('../locales/*/*.json', { eager: true });
const resources = {};
for (const path in catalogModules) {
  const match = path.match(/locales\/([^/]+)\/([^/]+)\.json$/);
  if (!match) continue;
  const [, locale, namespace] = match;
  if (!resources[locale]) resources[locale] = {};
  resources[locale][namespace] = catalogModules[path].default;
}

export function savedLanguage() {
  try {
    const value = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return SUPPORTED.includes(value) ? value : null;
  } catch {
    return null;
  }
}

// Best guess from the OS language list, used to pre-select the first-run
// screen. Spain/Portugal map to the European variants; every other region of
// the language maps to the Latin-American / Brazilian base.
export function osLanguageGuess() {
  const tags = [...(navigator.languages || []), navigator.language].filter(Boolean);
  for (const tag of tags) {
    const lower = String(tag).toLowerCase();
    if (lower.startsWith('en')) return 'en';
    if (lower === 'es-es') return 'es-ES';
    if (lower.startsWith('es')) return 'es-419';
    if (lower.startsWith('fr')) return 'fr';
    if (lower === 'pt-pt') return 'pt-PT';
    if (lower.startsWith('pt')) return 'pt-BR';
  }
  return 'en';
}

// Single entry point for changing language: i18next + persistence + the main
// process (dialog strings, spellchecker). Old preloads without setLanguage
// are tolerated — the renderer must never depend on the bridge existing.
export async function setLanguage(code) {
  const lang = SUPPORTED.includes(code) ? code : 'en';
  await i18n.changeLanguage(lang);
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
  } catch {
    // storage unavailable — in-memory language still applies
  }
  try {
    window.api?.setLanguage?.(lang);
  } catch {
    // bridge optional
  }
}

i18n.use(initReactI18next).init({
  resources,
  lng: savedLanguage() || 'en',
  supportedLngs: SUPPORTED,
  load: 'currentOnly',
  fallbackLng: {
    'es-ES': ['es-419', 'en'],
    'pt-PT': ['pt-BR', 'en'],
    default: ['en'],
  },
  defaultNS: 'common',
  interpolation: { escapeValue: false }, // React already escapes
  returnNull: false,
  returnEmptyString: false,
});

i18n.on('languageChanged', (lng) => {
  try {
    document.documentElement.lang = lng;
  } catch {
    // non-DOM environment
  }
});

try {
  document.documentElement.lang = i18n.language;
} catch {
  // non-DOM environment
}

// Keep the main process in sync from boot (updater dialogs can fire before
// the user ever opens settings).
try {
  window.api?.setLanguage?.(i18n.language);
} catch {
  // bridge optional
}

export default i18n;
